import { nowMs } from '../time/clock.ts'
import { type RandomBytes, webCryptoRandomBytes } from './random.ts'

/**
 * UUIDv7 — the primary-key format for every table in the system.
 *
 * `docs/ARCHITECTURE.md` §5: "Application-generated UUIDv7 text — sortable, no
 * `uuid-ossp`, no autoincrement collision on import". Sortable is the operative word: the
 * first 48 bits are the creation instant in milliseconds, so ids sort into creation order
 * and an index on the primary key stays append-mostly instead of scattering writes.
 *
 * Layout (RFC 9562 §5.7):
 *
 * ```
 * 0                   1                   2                   3
 * |unix_ts_ms (48 bits)           |ver|rand_a |var|rand_b (62 bits)  |
 * ```
 *
 * Generation lives in `packages/domain` rather than in `packages/db` deliberately — see
 * `docs/DECISIONS.md`, "Identifiers are UUIDv7, generated in packages/domain".
 */

/**
 * Canonical UUIDv7: **lowercase only**, version nibble `7`, RFC 4122 variant (`8`–`b`).
 *
 * Lowercase is enforced rather than normalised. Ids are stored as SQLite `TEXT` and
 * compared byte-for-byte, so an upper-cased id would silently match nothing; rejecting it
 * at the edge turns that into an explicit validation error instead of a mysterious
 * not-found.
 */
export const UUID_V7_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/

/** The largest instant a UUIDv7 can carry: 2^48 - 1 ms, some time in the year 10889. */
export const MAX_UUID_V7_TIMESTAMP_MS = 2 ** 48 - 1

/** Bytes of randomness a UUIDv7 carries: 74 bits, padded to whole bytes. */
export const UUID_V7_RANDOM_BYTES = 10

/** Whether a value is a canonical UUIDv7 string. */
export function isUuidV7(value: unknown): value is string {
  return typeof value === 'string' && UUID_V7_PATTERN.test(value)
}

/** The pieces a UUIDv7 is assembled from. */
export type UuidV7Parts = {
  /** Creation instant, epoch milliseconds UTC. */
  readonly timestampMs: number
  /** Exactly {@link UUID_V7_RANDOM_BYTES} bytes; the version and variant bits are overwritten. */
  readonly randomBytes: Uint8Array
}

/**
 * Assembles a UUIDv7 from an instant and a block of randomness.
 *
 * Pure: same parts in, same string out. `uuidV7()` is the thin wrapper that supplies the
 * ambient clock and CSPRNG, so every property of the format can be tested without either.
 */
export function formatUuidV7(parts: UuidV7Parts): string {
  const { timestampMs, randomBytes } = parts
  if (!Number.isInteger(timestampMs)) {
    throw new RangeError(`A UUIDv7 timestamp must be an integer, received ${timestampMs}.`)
  }
  if (timestampMs < 0 || timestampMs > MAX_UUID_V7_TIMESTAMP_MS) {
    throw new RangeError(
      `A UUIDv7 timestamp must be between 0 and ${MAX_UUID_V7_TIMESTAMP_MS}, received ${timestampMs}.`,
    )
  }
  if (randomBytes.length !== UUID_V7_RANDOM_BYTES) {
    throw new RangeError(
      `A UUIDv7 needs exactly ${UUID_V7_RANDOM_BYTES} random bytes, received ${randomBytes.length}.`,
    )
  }

  const timestampBytes: number[] = []
  let remaining = timestampMs
  for (let index = 0; index < 6; index += 1) {
    timestampBytes.unshift(remaining % 256)
    remaining = Math.floor(remaining / 256)
  }

  const hex = [...timestampBytes, ...randomBytes]
    .map((byte, index) => {
      // Byte 6 carries the version (0b0111) in its high nibble; byte 8 carries the
      // variant (0b10) in its top two bits. Both overwrite randomness, which is why the
      // caller supplies 10 bytes for 74 bits.
      if (index === 6) {
        return (byte & 0x0f) | 0x70
      }
      if (index === 8) {
        return (byte & 0x3f) | 0x80
      }
      return byte
    })
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

/** Overridable sources for {@link uuidV7}. Both default to the ambient runtime. */
export type UuidV7Options = {
  /** Instant source, epoch milliseconds UTC. Defaults to the system clock. */
  readonly now?: () => number
  /** Randomness source. Defaults to Web Crypto. */
  readonly randomBytes?: RandomBytes
}

/**
 * A new UUIDv7.
 *
 * One of the two impure functions in this package: it reads the clock and the CSPRNG.
 * Both are injectable, and every rule that consumes an id takes it as an argument, so
 * nothing downstream inherits the impurity.
 *
 * No intra-millisecond counter is kept. RFC 9562 permits either; two ids minted in the
 * same millisecond therefore sort by their random half rather than by creation order,
 * which is acceptable because nothing in this system orders by id at sub-millisecond
 * resolution — `created_at` exists for ordering that must be exact.
 */
export function uuidV7(options: UuidV7Options = {}): string {
  const { now = nowMs, randomBytes = webCryptoRandomBytes } = options
  return formatUuidV7({ timestampMs: now(), randomBytes: randomBytes(UUID_V7_RANDOM_BYTES) })
}

/**
 * The creation instant encoded in a UUIDv7, in epoch milliseconds UTC.
 *
 * @throws if the value is not a canonical UUIDv7 — a v4 id, for instance, carries random
 * bits where the timestamp belongs and would otherwise yield a plausible-looking lie.
 */
export function uuidV7TimestampMs(value: string): number {
  if (!isUuidV7(value)) {
    throw new TypeError(`Not a canonical UUIDv7: ${JSON.stringify(value)}`)
  }
  return Number.parseInt(value.slice(0, 8) + value.slice(9, 13), 16)
}
